import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../api";
import { today } from "../utils";

const LETTER_TYPES = [
  "Supply Letter", "EMD Refund Request", "Bill Submission", "Authority Letter",
  "Tender Cover Letter", "Payment Reminder", "General",
];

const TEMPLATES = {
  "Supply Letter": "We are pleased to inform you that the goods against your order/tender referenced above have been dispatched. Kindly find the enclosed invoice and delivery challan for your records.\n\nWe request you to acknowledge receipt and process the payment as per the agreed terms.",
  "EMD Refund Request": "With reference to the above tender, we had submitted our Earnest Money Deposit (EMD) at the time of bid submission. As the tender process now stands concluded, we request you to kindly process the refund of our EMD at the earliest.",
  "Bill Submission": "Please find enclosed our bill(s) for the supply made against the above reference. We request you to kindly process the same for payment at your earliest convenience.",
  "Authority Letter": "This is to authorize the bearer of this letter to collect/submit documents and represent Shree Gauri Pharma in matters related to the above reference on our behalf.",
  "Tender Cover Letter": "We are submitting herewith our technical and financial bid for the above-referenced tender, along with all supporting documents as per the tender terms and conditions. We look forward to your favourable consideration.",
  "Payment Reminder": "This is a gentle reminder that payment against our bill(s) referenced above is still outstanding. We request you to kindly arrange payment at the earliest to avoid any inconvenience.",
  "General": "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="sgp-label">{label}</label>
    {children}
  </div>
);

export default function LetterForm() {
  const [form, setForm] = useState({
    letter_type: "Supply Letter", addressee_name: "", addressee_designation: "",
    to_org: "", to_address: "", date: today(), subject: "", body: TEMPLATES["Supply Letter"],
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickType = (t) => setForm((f) => ({ ...f, letter_type: t, body: f.body && f.body !== TEMPLATES[f.letter_type] ? f.body : TEMPLATES[t] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) return toast.error("Subject and letter body are required");
    setSaving(true);
    try {
      const { data } = await api.post("/letters", form);
      toast.success(`Letter ${data.ref_no} created`);
      navigate(`/print/letter/${data.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="letter-form-page">
      <div className="flex items-center gap-3">
        <Link to="/letters" className="text-slate-500 hover:text-teal-700"><ArrowLeft className="h-5 w-5" /></Link>
        <div>
          <h1 className="text-2xl font-heading font-bold tracking-tight text-slate-900">Write a Letter</h1>
          <p className="text-sm text-slate-500 mt-0.5">On SGP letterhead — ref. number, seal and signature block added automatically.</p>
        </div>
      </div>

      <form onSubmit={submit} className="sgp-card p-6 space-y-4">
        <Field label="Letter Type">
          <select className="sgp-input" value={form.letter_type} onChange={(e) => pickType(e.target.value)} data-testid="letter-type">
            {LETTER_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Date"><input type="date" className="sgp-input" value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
          <Field label="Addressee Designation (e.g. The Civil Surgeon)"><input className="sgp-input" value={form.addressee_designation} onChange={(e) => set("addressee_designation", e.target.value)} /></Field>
          <Field label="Addressee Name"><input className="sgp-input" value={form.addressee_name} onChange={(e) => set("addressee_name", e.target.value)} /></Field>
          <Field label="Organization"><input className="sgp-input" value={form.to_org} onChange={(e) => set("to_org", e.target.value)} /></Field>
        </div>
        <Field label="Address"><textarea rows={2} className="sgp-input h-auto py-2" value={form.to_address} onChange={(e) => set("to_address", e.target.value)} /></Field>
        <Field label="Subject"><input required className="sgp-input" data-testid="letter-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} /></Field>
        <Field label="Letter Body">
          <textarea required rows={10} className="sgp-input h-auto py-2 font-normal leading-relaxed" data-testid="letter-body"
            value={form.body} onChange={(e) => set("body", e.target.value)} />
        </Field>

        <button type="submit" disabled={saving} data-testid="letter-save-btn"
          className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50">
          <Send className="h-4 w-4" /> {saving ? "Saving..." : "Save & Preview"}
        </button>
      </form>
    </div>
  );
}
